import type { CartLine, CartState } from './cart';
import type { DocumentHeader } from './editorSchema';
import { dec, decimalPlaces, isDecimal, max, mulDivRound, toPlain, ZERO } from './decimal';
import { effectiveDiscount, type Discount } from './totals';

/** Item lines for POST/PATCH sales orders and invoices. The price is always sent so it never drifts. */
export function toLineRequests(state: CartState) {
  return state.lines.map((l) => {
    const discount = effectiveDiscount(l.discount);
    const price = isDecimal(l.unitPrice) && decimalPlaces(l.unitPrice) <= 4;
    return {
      variantId: l.variantId,
      unitId: l.unitId,
      qty: l.qty,
      ...(price ? { unitPrice: l.unitPrice } : {}),
      ...(discount ? { discount } : {}),
      taxRateId: l.taxRateId,
    };
  });
}

interface DocLine {
  id: string;
  variantId: string;
  sku: string;
  description: string;
  unitId: string;
  unitSymbol: string;
  factor: string;
  qty: string;
  unitPrice: string;
  discount?: Discount | null;
  taxRateId: string | null;
  taxRate: string;
}

/** A saved document's lines as an editable cart (to edit a draft order or copy a document). */
export function cartFromDocument(doc: { lines: DocLine[]; discount?: Discount | null }): CartState {
  return {
    lines: doc.lines.map((l): CartLine => ({
      key: l.id,
      variantId: l.variantId,
      productId: '',
      basePrice:
        dec(l.factor) > 0n
          ? toPlain(mulDivRound(dec(l.unitPrice), dec('1'), dec(l.factor), 8))
          : toPlain(dec(l.unitPrice)),
      name: l.description,
      sku: l.sku,
      barcode: null,
      unitId: l.unitId,
      unitSymbol: l.unitSymbol,
      factor: l.factor,
      allowsFraction: true,
      listPrice: toPlain(dec(l.unitPrice)),
      qty: toPlain(dec(l.qty)),
      unitPrice: toPlain(dec(l.unitPrice)),
      discount: l.discount ?? null,
      taxRateId: l.taxRateId,
      taxRate: l.taxRate,
    })),
    discount: doc.discount ?? null,
    revision: 0,
  };
}

/** Invoice chip: cancelled wins, then overdue, then the payment status. */
export function invoiceStatus(inv: { status: string; paymentStatus: string; overdue: boolean }): {
  status: string;
  label: string;
} {
  if (inv.status === 'CANCELLED') return { status: 'CANCELLED', label: 'Cancelled' };
  if (inv.status === 'DRAFT') return { status: 'DRAFT', label: 'Draft' };
  if (inv.overdue) return { status: 'OVERDUE', label: 'Overdue' };
  const labels: Record<string, string> = {
    PAID: 'Paid',
    PARTIALLY_PAID: 'Part paid',
    UNPAID: 'Unpaid',
  };
  return { status: inv.paymentStatus, label: labels[inv.paymentStatus] ?? inv.paymentStatus };
}

export interface OrderActions {
  edit: boolean;
  confirm: boolean;
  deliver: boolean;
  invoice: boolean;
  close: boolean;
  cancel: boolean;
  reopen: boolean;
}

interface OrderLike {
  status: string;
  lines: {
    qtyDelivered: string;
    qtyInvoiced: string;
    qtyToDeliver: string;
    qtyToInvoice: string;
  }[];
}

/** What can be done with an order now, given its state and the user's permissions. */
export function orderActions(order: OrderLike, can: (p: string) => boolean): OrderActions {
  const s = order.status;
  const open = s === 'CONFIRMED' || s === 'PARTIALLY_DELIVERED';
  const touched = order.lines.some((l) => dec(l.qtyDelivered) > 0n || dec(l.qtyInvoiced) > 0n);
  const toDeliver = order.lines.some((l) => dec(l.qtyToDeliver) > 0n);
  const toInvoice = order.lines.some((l) => dec(l.qtyToInvoice) > 0n);
  return {
    edit: s === 'DRAFT' && can('sales.create'),
    confirm: s === 'DRAFT' && can('sales.confirm'),
    deliver: open && toDeliver && can('sales.deliver'),
    invoice: s !== 'DRAFT' && s !== 'CANCELLED' && toInvoice && can('sales.invoice'),
    close: open && can('sales.cancel'),
    cancel: (s === 'DRAFT' || s === 'CONFIRMED') && !touched && can('sales.cancel'),
    reopen: s === 'CONFIRMED' && !touched && can('sales.confirm'),
  };
}

/** Share of an order line that is done, 0–100 (delivered or invoiced, both in base units). */
export function progress(doneBase: string, qtyBase: string): number {
  const total = dec(qtyBase);
  if (total <= 0n) return 0;
  const pct = Number((max(dec(doneBase), ZERO) * 100n) / total);
  return Math.min(100, Math.max(0, pct));
}

export interface QtyRow {
  lineId: string;
  /** In the line unit. */
  remaining: string;
  qty: string;
}

/** Problems with the quantities chosen in the deliver / invoice dialog. */
export function qtyRowsProblems(rows: QtyRow[], noun: 'deliver' | 'invoice'): string[] {
  const problems: string[] = [];
  let any = false;
  for (const r of rows) {
    if (r.qty.trim() === '') continue;
    if (!isDecimal(r.qty) || decimalPlaces(r.qty) > 4) {
      problems.push('Enter quantities as numbers with at most 4 decimals.');
      continue;
    }
    if (dec(r.qty) > dec(r.remaining))
      problems.push(`You cannot ${noun} more than what is left on the order.`);
    if (dec(r.qty) > 0n) any = true;
  }
  if (!any) problems.push(`Enter a quantity to ${noun} on at least one line.`);
  return [...new Set(problems)];
}

/** The rows to send (zero and empty quantities are left out). */
export function qtyRowsRequest(rows: QtyRow[]): { salesOrderLineId: string; qty: string }[] {
  return rows
    .filter((r) => isDecimal(r.qty) && dec(r.qty) > 0n)
    .map((r) => ({ salesOrderLineId: r.lineId, qty: toPlain(dec(r.qty)) }));
}

/** "1 line", "3 lines". */
export function plural(n: number, one: string, many = `${one}s`): string {
  return `${String(n)} ${n === 1 ? one : many}`;
}

/** The editor header of a Duplicate: the copied document's customer, location and notes; today's date. */
export function copyHeader(doc: {
  contact: { id: string; name: string; phone: string | null };
  location?: { id: string } | null;
  notes?: string | null;
}): Partial<DocumentHeader> {
  return {
    contact: { id: doc.contact.id, name: doc.contact.name, phone: doc.contact.phone },
    ...(doc.location ? { locationId: doc.location.id } : {}),
    notes: doc.notes ?? '',
  };
}
